import API_BASE from '../config';
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText, Presentation, Lock,
  CheckCircle2, Clock, ChevronRight,
  FileSearch, Loader2, AlertTriangle, Crown, Download
} from "lucide-react";
import UpgradeGate from "../components/UpgradeGate";

// ─── Types ────────────────────────────────────────────────────────────────────

interface ReportLog {
  id:            number;
  report_type:   string;
  generated_at:  string;
  verify_id?:    string;
  company_name?: string;
  tier_at_gen?:  string;
}

// ─── Report feature lists ─────────────────────────────────────────────────────

const starterFeatures = [
  "8-page starter audit report",
  "Financial exposure analysis & chart",
  "External threat surface monitoring",
  "Regulatory compliance radar",
  "1-page strategic remediation",
  "Official audit certification & seal",
];

const professionalFeatures = [
  "Full 13-page executive audit report",
  "Financial exposure analysis & chart",
  "Monte Carlo P90/P99 confidence intervals",
  "External threat surface intelligence",
  "Vendor risk management matrix",
  "Regulatory compliance status",
  "Insurance ROI & sector benchmarking",
  "3-page Vicshield strategic remediation",
  "Executive ROI summary",
  "Official audit certification & seal",
];

const enterpriseFeatures = [
  "18-slide board-ready intelligence deck",
  "Full actuarial risk math with confidence intervals",
  "External threat intelligence (unmasked identities)",
  "Vendor & supply chain risk analysis",
  "Regulatory compliance deep-dive",
  "Insurance & sector benchmarking",
  "4-slide Vicshield strategic remediation",
  "Phased 12-month timeline",
  "Technical roadmap & telemetry sync",
  "Board disclosure & executive mandate",
  "Official audit certification & seal",
];

// ─── Download button state ────────────────────────────────────────────────────

type DownloadState = "idle" | "loading" | "success" | "error";

// ─── Main Component ───────────────────────────────────────────────────────────

export default function Reports() {
  const [history,          setHistory]          = useState<ReportLog[]>([]);
  const [historyLoading,   setHistoryLoading]   = useState(true);
  const [stdState,         setStdState]         = useState<DownloadState>("idle");
  const [entState,         setEntState]         = useState<DownloadState>("idle");
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [redownloadingId,  setRedownloadingId]  = useState<number | null>(null);

  // Fetch user profile for tier gating
  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ["user-profile"],
    queryFn:  () =>
      axios.get(`${API_BASE}/api/auth/me`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      }).then((res) => res.data),
    enabled: !!localStorage.getItem("token"),
  });

  const isEnterprise =
    (userData?.subscription_tier || userData?.plan)?.toLowerCase() === "enterprise";
  const isProfessional =
    (userData?.subscription_tier || userData?.plan)?.toLowerCase() === "professional";
  const tier = (userData?.subscription_tier || userData?.plan)?.toLowerCase() || "standard";
  const tierLabel = tier === "enterprise" ? "Enterprise" : tier === "professional" ? "Professional" : "Starter";

  // Fetch report history
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/reports/history`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      const data = Array.isArray(res.data) ? res.data : [];
      setHistory(data);
    } catch (err) {
      console.error("Failed to fetch report history:", err);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, []);

  const handleRedownload = async (log: ReportLog) => {
    setRedownloadingId(log.id);
    try {
      const isEnt = log.report_type?.toLowerCase().includes("enterprise") ||
                    log.report_type?.toLowerCase().includes("board");
      const endpoint = isEnt
        ? `${API_BASE}/assessment/download-enterprise`
        : `${API_BASE}/assessment/download`;
      const res = await axios.post(endpoint, {}, {
        headers:      { Authorization: `Bearer ${localStorage.getItem("token")}` },
        responseType: "blob",
      });
      const url      = window.URL.createObjectURL(new Blob([res.data]));
      const link     = document.createElement("a");
      const filename = isEnt ? "VicShield_Board_Deck.pdf" : "VicShield_Audit_Report.pdf";
      link.href      = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { /* silent */ }
    finally { setRedownloadingId(null); }
  };

  // Download standard PDF
  const handleDownloadStandard = async () => {
    setStdState("loading");
    try {
      const res = await axios.post(
        `${API_BASE}/assessment/download`,
        {},
        {
          headers:      { Authorization: `Bearer ${localStorage.getItem("token")}` },
          responseType: "blob",
        }
      );
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href  = url;
      link.setAttribute("download", "VicShield_Standard_Report.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      setStdState("success");
      fetchHistory();
      setTimeout(() => setStdState("idle"), 3000);
    } catch {
      setStdState("error");
      setTimeout(() => setStdState("idle"), 3000);
    }
  };

  // Download enterprise deck
  const handleDownloadEnterprise = async () => {
    if (!isEnterprise) {
      setShowUpgradeModal(true);
      return;
    }
    setEntState("loading");
    try {
      const res = await axios.post(
        `${API_BASE}/assessment/download-enterprise`,
        {},
        {
          headers:      { Authorization: `Bearer ${localStorage.getItem("token")}` },
          responseType: "blob",
        }
      );
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href  = url;
      link.setAttribute("download", "VicShield_Board_Deck.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      setEntState("success");
      fetchHistory();
      setTimeout(() => setEntState("idle"), 3000);
    } catch {
      setEntState("error");
      setTimeout(() => setEntState("idle"), 3000);
    }
  };

  // Trial check — block access for standard/trial users
  // Wait for user data to load before gating to prevent flash to pricing
  if (userLoading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-red-600 border-t-transparent
            rounded-full animate-spin" />
          <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
            Loading Reports...
          </p>
        </div>
      </div>
    );
  }
  const isTrial = tier === "standard";

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-4 md:p-6 space-y-5 bg-[#f8fafc] min-h-screen font-['Inter']"
    >
      {/* Trial Preview Banner */}
      {isTrial && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Clock size={16} className="text-amber-600 flex-shrink-0" />
            <div>
              <p className="text-xs font-black text-amber-800 uppercase tracking-widest">Trial Preview Mode</p>
              <p className="text-[11px] text-amber-700 font-medium mt-0.5">
                You can see what reports are available. Upgrade to download your full audit PDF and board intelligence deck.
              </p>
            </div>
          </div>
          <button
            onClick={() => window.location.href = '/pricing'}
            className="flex-shrink-0 px-4 py-2 bg-amber-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest hover:bg-amber-700 transition-all"
          >
            Upgrade →
          </button>
        </div>
      )}
      {/* ── PAGE HEADER ────────────────────────────────────────────── */}
      <div>
        <p className="text-[9px] font-black uppercase tracking-[0.3em] text-red-600 mb-1">
          Intelligence Vault
        </p>
        <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">
          Audit Reports
        </h1>
        <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">
          Download your actuarial intelligence documents
        </p>
      </div>

      {/* ── REPORT CARDS ─────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

        {/* ── STARTER / STANDARD REPORT CARD ───────────────────────── */}
        <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[3rem] border border-white/10 overflow-hidden
          flex flex-col hover:border-slate-200 hover:shadow-xl transition-all duration-300">

          {/* Card header */}
          <div className="p-8 border-b border-white/10">
            <div className="flex items-start justify-between mb-6">
              <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center
                justify-center shadow-lg">
                <FileText size={26} className="text-white" />
              </div>
              <span className="text-[9px] font-black uppercase tracking-widest
                px-3 py-1.5 rounded-full border text-emerald-400 bg-emerald-400/10
                border-emerald-400/20">
                {tier === "starter" ? "Starter" : "Starter"} Plan
              </span>
            </div>
            <h2 className="text-xl font-black text-white uppercase tracking-tight mb-2">
              Starter Audit Report
            </h2>
            <p className="text-xs font-medium text-slate-400 leading-relaxed">
              An 8-page audit report covering financial exposure, threat surface,
              regulatory compliance and strategic remediation.
            </p>
          </div>

          {/* Features */}
          <div className="px-8 py-6 flex-1">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-4">
              What's included
            </p>
            <ul className="space-y-2.5">
              {starterFeatures.map((f, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle2 size={13} className="text-emerald-500 flex-shrink-0" />
                  <span className="text-xs font-bold text-slate-300">{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Download button */}
          <div className="px-8 pb-8">
            <DownloadButton
              state={stdState}
              onClick={isTrial ? () => window.location.href = '/pricing' : handleDownloadStandard}
              label={isTrial ? "Upgrade to Download" : "Download Starter Report"}
              variant="dark"
            />
          </div>
        </div>

        {/* ── PROFESSIONAL REPORT CARD ──────────────────────────────── */}
        <div className={`rounded-[3rem] border overflow-hidden flex flex-col
          transition-all duration-300 ${
          isProfessional || isEnterprise
            ? "bg-gradient-to-br from-[#0f172a] to-[#1e293b] border-blue-500/30 hover:border-blue-400/50"
            : "bg-gradient-to-br from-[#0f172a] to-[#1e293b] border-white/10 hover:border-white/20"
          }`}>

          {/* Card header */}
          <div className="p-8 border-b border-white/10">
            <div className="flex items-start justify-between mb-6">
              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg
                ${isProfessional || isEnterprise ? "bg-blue-600" : "bg-slate-200"}`}>
                <FileText size={26} className="text-white" />
              </div>
              <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5
                rounded-full border flex items-center gap-1.5 ${
                isProfessional || isEnterprise
                  ? "text-blue-400 bg-blue-400/10 border-blue-400/20"
                  : "text-slate-400 bg-slate-100 border-slate-200"
              }`}>
                {isProfessional || isEnterprise
                  ? <><CheckCircle2 size={10} /> Unlocked</>
                  : <><Lock size={10} /> Professional+</>
                }
              </span>
            </div>
            <h2 className="text-xl font-black text-white uppercase tracking-tight mb-2">
              Executive Audit Report
            </h2>
            <p className="text-xs font-medium text-slate-400 leading-relaxed">
              Full 13-page actuarial audit with Monte Carlo intervals, vendor risk,
              insurance intelligence and executive ROI summary.
            </p>
          </div>

          {/* Features */}
          <div className="px-8 py-6 flex-1">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-4">
              What's included
            </p>
            <ul className="space-y-2.5">
              {professionalFeatures.map((f, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle2 size={13} className={`flex-shrink-0 ${
                    isProfessional || isEnterprise ? "text-blue-500" : "text-slate-300"
                  }`} />
                  <span className={`text-xs font-bold ${
                    isProfessional || isEnterprise ? "text-slate-200" : "text-slate-400"
                  }`}>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Download / upgrade button */}
          <div className="px-8 pb-8">
            {isProfessional || isEnterprise ? (
              <DownloadButton
                state={stdState}
                onClick={handleDownloadStandard}
                label="Download Executive Report"
                variant="dark"
              />
            ) : (
              <button
                onClick={async () => {
                  const token = localStorage.getItem("token");
                  const res = await fetch(`${API_BASE}/api/stripe/create-checkout`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({ tier: "professional", billing: "monthly" }),
                  });
                  const data = await res.json();
                  if (data.checkout_url) document.location.assign(data.checkout_url);
                }}
                className="w-full py-4 rounded-2xl font-black text-[11px] uppercase
                  tracking-widest bg-blue-600 text-white hover:bg-blue-700
                  transition-all flex items-center justify-center gap-2"
              >
                <Lock size={13} /> Upgrade to Professional
              </button>
            )}
          </div>
        </div>

        {/* ── ENTERPRISE DECK CARD ──────────────────────────────────── */}
        <div className="bg-[#0f172a] rounded-[3rem] border-2 border-[#0f172a] overflow-hidden
          flex flex-col relative hover:border-red-600/40 hover:shadow-2xl
          hover:shadow-red-900/20 transition-all duration-300">

          {/* Subtle glow */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
            w-[400px] h-[400px] bg-red-600 opacity-[0.05] rounded-full blur-[100px]
            pointer-events-none" />

          {/* Card header */}
          <div className="relative z-10 p-8 border-b border-white/5">
            <div className="flex items-start justify-between mb-6">
              <div className="w-14 h-14 bg-red-600 rounded-2xl flex items-center
                justify-center shadow-lg shadow-red-900/40">
                <Presentation size={26} className="text-white" />
              </div>
              <span className={`text-[9px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full flex items-center gap-1.5 ${
                isEnterprise
                  ? "text-emerald-400 bg-emerald-400/10 border border-emerald-400/20"
                  : "text-amber-400 bg-amber-400/10 border border-amber-400/20"
              }`}>
                {isEnterprise ? (
                  <><CheckCircle2 size={10} /> Unlocked</>
                ) : (
                  <><Crown size={10} /> Enterprise Only</>
                )}
              </span>
            </div>
            <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-2">
              Board Intelligence Deck
            </h2>
            <p className="text-sm font-medium text-slate-400 leading-relaxed">
              An 18-slide landscape intelligence deck for executive presentations.
              High-density visuals with full AI narrative.
            </p>
          </div>

          {/* Features */}
          <div className="relative z-10 px-10 py-8 flex-1">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-600 mb-4">
              What's included
            </p>
            <ul className="space-y-3">
              {enterpriseFeatures.map((f, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckCircle2 size={14} className={`flex-shrink-0 ${
                    isEnterprise ? "text-red-500" : "text-slate-600"
                  }`} />
                  <span className={`text-xs font-bold ${
                    isEnterprise ? "text-slate-300" : "text-slate-600"
                  }`}>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Download / upgrade button */}
          <div className="relative z-10 px-10 pb-10">
            {isEnterprise ? (
              <DownloadButton
                state={entState}
                onClick={handleDownloadEnterprise}
                label="Generate Board Deck"
                variant="red"
              />
            ) : (
              <button
                onClick={() => setShowUpgradeModal(true)}
                className="w-full py-4 rounded-2xl font-black text-[11px] uppercase
                  tracking-widest transition-all flex items-center justify-center gap-2
                  bg-gradient-to-r from-amber-400 to-yellow-500 text-amber-950
                  hover:brightness-105 shadow-lg shadow-amber-900/20 active:scale-[0.98]"
              >
                <Lock size={14} />
                Upgrade to Enterprise to Unlock
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── AUDIT HISTORY ───────────────────────────────────────────── */}
      <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-[3rem] border border-white/10 overflow-hidden">

        {/* History header */}
        <div className="flex items-center gap-4 px-10 py-7 border-b border-white/10">
          <div className="w-10 h-10 bg-red-600 rounded-xl flex items-center
            justify-center shadow-md">
            <Clock size={18} className="text-white" />
          </div>
          <div>
            <h3 className="text-base font-black text-white uppercase tracking-tight">
              Intelligence Audit Trail
            </h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
              {history.length} report{history.length !== 1 ? "s" : ""} generated
            </p>
          </div>
        </div>

        {/* History list */}
        <div className="px-10 py-6">
          {historyLoading ? (
            <div className="space-y-3">
              {[1,2,3].map(i => (
                <div key={i} className="flex items-center justify-between p-5
                  bg-slate-50 rounded-2xl border border-slate-100 animate-pulse">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-slate-200 rounded-xl" />
                    <div className="space-y-2">
                      <div className="h-3 w-40 bg-slate-200 rounded-full" />
                      <div className="h-2 w-28 bg-slate-100 rounded-full" />
                    </div>
                  </div>
                  <div className="h-3 w-20 bg-slate-200 rounded-full" />
                </div>
              ))}
            </div>
          ) : history.length > 0 ? (
            <div className="space-y-3">
              {history.map((log) => {
                const isEntDeck = log.report_type?.toLowerCase().includes("enterprise") ||
                                  log.report_type?.toLowerCase().includes("board");
                return (
                  <motion.div
                    key={log.id}
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center justify-between p-5 bg-white/5
                      rounded-2xl border border-white/10 hover:border-red-500/30
                      hover:bg-red-50/20 transition-all group"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        isEntDeck
                          ? "bg-slate-900 text-white"
                          : "bg-white/10 text-white border border-white/20"
                      }`}>
                        {isEntDeck
                          ? <Presentation size={18} />
                          : <FileText size={18} />
                        }
                      </div>
                      <div>
                        <p className="text-xs font-black text-white uppercase tracking-tight">
                          {log.report_type}
                        </p>
                        <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest mt-0.5">
                          {log.verify_id
                            ? `Audit ID: ${log.verify_id}`
                            : `Audit ID: VS-${String(log.id).padStart(6,"0")}-${isEntDeck ? "ENT" : "STD"}`
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <p className="text-[10px] font-black text-slate-300 uppercase tracking-tight">
                          {new Date(log.generated_at).toLocaleDateString("en", {
                            day:   "2-digit",
                            month: "short",
                            year:  "numeric",
                          })}
                        </p>
                        <p className="text-[9px] font-bold text-emerald-500 uppercase tracking-widest mt-0.5">
                          Verified ✓
                        </p>
                      </div>
                      <button
                        onClick={() => handleRedownload(log)}
                        disabled={redownloadingId === log.id}
                        title="Re-download this report"
                        className="w-9 h-9 rounded-xl bg-white/10 hover:bg-red-600
                          flex items-center justify-center transition-all group
                          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {redownloadingId === log.id
                          ? <Loader2 size={15} className="text-slate-400 animate-spin" />
                          : <Download size={15} className="text-slate-400 group-hover:text-white transition-colors" />
                        }
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          ) : (
            <div className="py-16 text-center border-2 border-dashed border-white/10 rounded-2xl">
              <FileSearch size={40} className="mx-auto text-slate-200 mb-4" />
              <p className="text-xs font-black text-slate-400 uppercase tracking-widest">
                No reports generated yet
              </p>
              <p className="text-[10px] font-bold text-slate-300 uppercase tracking-widest mt-1">
                Download your first report above
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ── UPGRADE MODAL ───────────────────────────────────────────── */}
      <AnimatePresence>
        {showUpgradeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50
              flex items-center justify-center p-6"
            onClick={() => setShowUpgradeModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#0f172a] rounded-[3rem] p-12 max-w-lg w-full
                border-t-8 border-red-600 shadow-2xl"
            >
              <div className="w-14 h-14 bg-red-600/10 border border-red-600/20
                rounded-2xl flex items-center justify-center mb-8">
                <Crown size={26} className="text-amber-400" />
              </div>

              <h2 className="text-2xl font-black text-white uppercase tracking-tight mb-3">
                Enterprise Feature
              </h2>
              <p className="text-slate-400 text-sm font-medium leading-relaxed mb-8">
                The Board Intelligence Deck is exclusive to Enterprise subscribers.
                Upgrade to unlock the full 18-slide landscape presentation with
                AI-generated narrative and board-level visuals.
              </p>

              <div className="space-y-3 mb-10">
                {[
                  "18-slide board-ready intelligence deck",
                  "AI Compliance Intelligence per regulation",
                  "AI Insurance Intelligence & underwriting analysis",
                  "Unmasked dark web credential records",
                  "Sector benchmarking (IBM/Verizon DBIR 2024)",
                  "Priority support & dedicated account handler",
                ].map((b, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <CheckCircle2 size={14} className="text-red-500 flex-shrink-0" />
                    <span className="text-white text-sm font-bold">{b}</span>
                  </div>
                ))}
              </div>

              <a
                href="/settings"
                className="block w-full py-4 bg-red-600 text-white rounded-2xl
                  font-black text-[11px] uppercase tracking-widest text-center
                  hover:bg-white hover:text-red-600 transition-all shadow-xl
                  shadow-red-900/30"
              >
                Upgrade to Enterprise — $999/mo
              </a>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="block w-full py-3 mt-3 text-slate-500 font-black
                  text-[10px] uppercase tracking-widest text-center
                  hover:text-white transition-colors"
              >
                Maybe Later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.div>
  );
}

// ─── Download Button Component ────────────────────────────────────────────────

function DownloadButton({
  state, onClick, label, variant,
}: {
  state:   DownloadState;
  onClick: () => void;
  label:   string;
  variant: "dark" | "red";
}) {
  const base =
    "w-full py-4 rounded-2xl font-black text-[11px] uppercase tracking-widest " +
    "transition-all flex items-center justify-center gap-2 active:scale-[0.98] " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

  const colours =
    variant === "red"
      ? "bg-red-600 text-white hover:bg-white hover:text-red-600 shadow-lg shadow-red-900/30"
      : "bg-slate-900 text-white hover:bg-red-600 shadow-lg shadow-slate-900/10";

  const successColours = "bg-emerald-500 text-white cursor-default";
  const errorColours   = "bg-red-50 text-red-600 border-2 border-red-200 cursor-default";

  if (state === "success") {
    return (
      <motion.div
        initial={{ scale: 0.97 }}
        animate={{ scale: 1 }}
        className={`${base} ${successColours}`}
      >
        <CheckCircle2 size={16} /> Downloaded Successfully
      </motion.div>
    );
  }

  if (state === "error") {
    return (
      <div className={`${base} ${errorColours}`}>
        <AlertTriangle size={16} /> Generation Failed — Retry
      </div>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={state === "loading"}
      className={`${base} ${colours}`}
    >
      {state === "loading" ? (
        <><Loader2 size={16} className="animate-spin" /> Generating...</>
      ) : (
        <>{label} <ChevronRight size={15} /></>
      )}
    </button>
  );
}
